import { ReactNode } from 'react';

import { rem, Flex, Header, useColorModeValue } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { useSourceHoverControls } from '../../../../hooks/live/useSourceHoverControls';
import {
  EPollState,
  IPoll,
  IPollItemMenuActionItem,
  IPollItemActions,
} from '../../../../types/interaction_tools';
import { createDomName } from '../../../../utils/dom/identifying';
import { BokehMenu, BokehMenuItem } from '../../../bokeh';
import { PollItemActions } from './PollItemActions';
import { PollOptionVotesBar } from './PollOptionVotesBar';

interface IPollItemProps extends IBaseDomElementProps {
  poll: IPoll;
  isPollShownInActiveScene?: boolean;
  isAnyPollPublished?: boolean;
  canOpenPoll: boolean;
  menuAttach: string;
  itemActions: IPollItemActions;
  menuActions: Array<IPollItemMenuActionItem>;
  childrenControls?: ReactNode;
}

export function PollItem({
  className = createDomName('poll-item'),
  isPollShownInActiveScene,
  isAnyPollPublished,
  poll,
  poll: { state, question, options },
  canOpenPoll,
  menuAttach,
  itemActions,
  menuActions,
  childrenControls,
}: IPollItemProps) {
  const borderColor: string = useColorModeValue('stroke', 'transparent');
  const { onMouseEnter, onMouseLeave, isMenuOpen, isItemHovered, onToggleMenu, onCloseMenu } =
    useSourceHoverControls();

  const totalVotesCount: number = options.reduce((acc, option) => acc + option.votesCount, 0);
  const isPollCompleted: boolean = state === EPollState.CLOSED || state === EPollState.ARCHIVED;
  const isPollArchived: boolean = state === EPollState.ARCHIVED;

  return (
    <Flex
      className={className}
      position={'relative'}
      padding={rem(16)}
      rowGap={rem(16)}
      direction={'column'}
      background={'fill-component'}
      _hover={{
        background: 'fill-component-hover',
      }}
      border={'1px solid'}
      borderRadius={rem(8)}
      borderColor={borderColor}
      cursor={isPollCompleted ? 'default' : 'pointer'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Flex
        className={createDomName(className, 'header')}
        justifyContent={'space-between'}
        alignItems={'flex-start'}
      >
        <Header
          className={createDomName(className, 'question')}
          size={'xs'}
          minHeight={rem(24)}
          margin={0}
          maxWidth={isPollArchived ? '100%' : '83%'}
        >
          {question}
        </Header>

        <Flex
          className={createDomName(className, 'controls')}
          visibility={isItemHovered ? 'visible' : 'hidden'}
        >
          {isPollArchived ? null : (
            <>
              {childrenControls}

              {!menuActions.length ? null : (
                <BokehMenu
                  onClose={onCloseMenu}
                  placement={menuAttach}
                  className={createDomName(className, 'menu', 'button')}
                  isOpen={isMenuOpen}
                  onClick={onToggleMenu}
                  menuList={
                    <>
                      {menuActions.map(({ label, key, onClick, icon }) => (
                        <BokehMenuItem
                          key={key}
                          className={createDomName(className, 'menu', 'item')}
                          onClick={() => {
                            if (onClick) {
                              onClick();
                            }

                            if (!['removeFromScene', 'addToScene'].includes(key)) {
                              onCloseMenu();
                            }
                          }}
                          icon={icon as React.ReactElement}
                        >
                          {label}
                        </BokehMenuItem>
                      ))}
                    </>
                  }
                />
              )}
            </>
          )}
        </Flex>
      </Flex>

      {options.map((opt) => (
        <PollOptionVotesBar
          className={createDomName(className, 'votes')}
          key={opt.id}
          option={opt}
          state={state}
          totalCount={totalVotesCount}
        />
      ))}

      <PollItemActions
        isPollShownInActiveScene={isPollShownInActiveScene}
        poll={poll}
        canOpenPoll={canOpenPoll}
        itemActions={itemActions}
        isAnyPollPublished={isAnyPollPublished}
      />
    </Flex>
  );
}
